import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Car, Clock, MapPin, Star } from "lucide-react";

const stats = [
  { icon: Clock, value: 24, suffix: "h/24", label: "Disponibilité" },
  { icon: Car, value: 7, suffix: "j/7", label: "Jours par semaine" },
  { icon: MapPin, value: 24, suffix: "+", label: "Communes Nord Toulouse" },
  { icon: Star, value: 100, suffix: "%", label: "Satisfaction client" },
];

function AnimatedCounter({ target, suffix, inView }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1500;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, inView]);

  return (
    <span className="text-4xl md:text-5xl font-bold text-gold-gradient font-tagline">
      {count}{suffix}
    </span>
  );
}

export default function StatsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-16 md:py-20" ref={ref} data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-card/80 backdrop-blur-md border border-amber-500/20 rounded-3xl p-8 md:p-12 shadow-2xl"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                className="flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 mb-4">
                  <s.icon className="h-6 w-6 text-amber-400" />
                </div>
                <AnimatedCounter target={s.value} suffix={s.suffix} inView={inView} />
                <span className="text-sm text-zinc-500 mt-2">{s.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
